import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Paper, CircularProgress, LinearProgress,
    Alert, Chip, Button, TextField, Autocomplete
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDropzone } from 'react-dropzone';
import { useTheme } from '@mui/material/styles';
import { vortxColors } from '../core';
import { useExtraction } from './hooks/useExtraction';
import { DOCUMENT_TYPES } from './config/documentTypes';

const OTHER_OPTION = {
    value: '_other',
    label: 'Other...',
    icon: 'Description',
    description: 'Custom document type',
};

/**
 * Parse an acceptedFileTypes string like ".pdf,.csv" into a react-dropzone accept object.
 * Example: ".pdf,.csv" -> { 'application/pdf': ['.pdf'], 'text/csv': ['.csv'] }
 */
function parseAcceptedFileTypes(acceptStr) {
    if (!acceptStr) return undefined;

    const mimeMap = {
        '.pdf': 'application/pdf',
        '.csv': 'text/csv',
        '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.xls': 'application/vnd.ms-excel',
        '.json': 'application/json',
        '.txt': 'text/plain',
        '.png': 'image/png',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
    };

    const accept = {};
    acceptStr.split(',').forEach(ext => {
        const trimmed = ext.trim().toLowerCase();
        const mime = mimeMap[trimmed] || 'application/octet-stream';
        if (!accept[mime]) accept[mime] = [];
        if (!accept[mime].includes(trimmed)) accept[mime].push(trimmed);
    });
    return accept;
}

export function DocumentUploader({
    defaultDocumentType,
    allowedDocumentTypes,
    hideDocumentType = false,
    projectId,
    metadata,
    onExtractionComplete,
    onError: onErrorProp,
    title = 'Upload Document',
    description: descriptionText,
    acceptedFileTypes = '.pdf,.csv',
}) {
    // Document type selection state
    const [selectedType, setSelectedType] = useState(() => {
        if (defaultDocumentType) {
            const found = DOCUMENT_TYPES.find(t => t.value === defaultDocumentType);
            return found || null;
        }
        return null;
    });
    const [customLabel, setCustomLabel] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Build the options list: filtered DOCUMENT_TYPES + "Other"
    const typeOptions = useMemo(() => {
        let types = DOCUMENT_TYPES;
        if (allowedDocumentTypes && allowedDocumentTypes.length > 0) {
            types = DOCUMENT_TYPES.filter(t => allowedDocumentTypes.includes(t.value));
        }
        return [...types, OTHER_OPTION];
    }, [allowedDocumentTypes]);

    // Extraction hook
    const {
        upload, reset, status, progress, statusMessage, error,
    } = useExtraction({
        onComplete: onExtractionComplete,
        onError: onErrorProp,
    });

    const isOtherSelected = selectedType?.value === '_other';

    // Determine the document type value to send
    const resolvedDocumentType = isOtherSelected
        ? (customLabel.trim() || '_other')
        : (selectedType?.value || '');

    // Drop handler
    const onDrop = async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setSelectedFile(file);

        const docType = hideDocumentType ? (defaultDocumentType || 'unknown') : resolvedDocumentType;
        await upload(file, docType, {
            customLabel: isOtherSelected ? customLabel.trim() : undefined,
            projectId,
            metadata,
        });
    };

    // Parse accepted file types for react-dropzone
    const acceptObj = useMemo(() => parseAcceptedFileTypes(acceptedFileTypes), [acceptedFileTypes]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: acceptObj,
        disabled: status === 'uploading' || status === 'processing',
        multiple: false,
    });

    // Reset everything for another upload
    const handleReset = () => {
        reset();
        setSelectedFile(null);
        if (!defaultDocumentType) {
            setSelectedType(null);
        }
        setCustomLabel('');
    };

    // --- IDLE STATE ---
    if (status === 'idle') {
        return (
            <Box>
                {/* Document Type Selection */}
                {!hideDocumentType && (
                    <Box sx={{ mb: 3 }}>
                        <Autocomplete
                            options={typeOptions}
                            getOptionLabel={(opt) => opt.label || ''}
                            value={selectedType}
                            onChange={(_, newValue) => {
                                setSelectedType(newValue);
                                if (newValue?.value !== '_other') setCustomLabel('');
                            }}
                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                            renderOption={(props, option) => {
                                const { key, ...restProps } = props;
                                return (
                                    <li key={key} {...restProps}>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                                {option.label}
                                            </Typography>
                                            {option.description && (
                                                <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                                    {option.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Document Type"
                                    placeholder="Select document type..."
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&.Mui-focused fieldset': {
                                                borderColor: vortxColors.gold,
                                                borderWidth: 2,
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: vortxColors.gold,
                                        },
                                    }}
                                />
                            )}
                            sx={{ minWidth: 280 }}
                        />

                        {/* Custom label input when "Other" is selected */}
                        {isOtherSelected && (
                            <TextField
                                label="Custom Document Label"
                                value={customLabel}
                                onChange={(e) => setCustomLabel(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="e.g. Demand Response Agreement"
                                sx={{
                                    mt: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': {
                                            borderColor: vortxColors.gold,
                                            borderWidth: 2,
                                        },
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: vortxColors.gold,
                                    },
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* File Drop Zone */}
                <Paper
                    {...getRootProps()}
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        border: '2px dashed',
                        borderColor: isDragActive ? vortxColors.gold : vortxColors.teal,
                        bgcolor: isDragActive ? 'rgba(178, 140, 80, 0.1)' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: vortxColors.gold,
                            bgcolor: 'rgba(178, 140, 80, 0.05)',
                        },
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUploadIcon sx={{ fontSize: 60, color: vortxColors.stone, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: vortxColors.white }}>
                        {isDragActive ? 'Drop file here' : title}
                    </Typography>
                    {descriptionText && (
                        <Typography variant="body2" sx={{ color: vortxColors.stone, mt: 1 }}>
                            {descriptionText}
                        </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: vortxColors.stone, mt: 1, display: 'block' }}>
                        Accepted: {acceptedFileTypes}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // --- UPLOADING / PROCESSING STATE ---
    if (status === 'uploading' || status === 'processing') {
        return (
            <Paper sx={{ p: 4, bgcolor: vortxColors.charcoal }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <CircularProgress size={50} sx={{ color: vortxColors.gold, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: vortxColors.white }}>
                        {status === 'uploading' ? 'Uploading...' : 'Processing Document'}
                    </Typography>
                </Box>

                {selectedFile && (
                    <Chip
                        label={selectedFile.name}
                        size="small"
                        sx={{
                            mb: 2,
                            display: 'block',
                            mx: 'auto',
                            width: 'fit-content',
                            bgcolor: 'rgba(178, 140, 80, 0.15)',
                            color: vortxColors.white,
                        }}
                    />
                )}

                {status === 'processing' && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                {statusMessage || 'Processing...'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                {progress}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 8,
                                borderRadius: 1,
                                bgcolor: isDark ? 'rgba(178, 140, 80, 0.2)' : '#333',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: vortxColors.gold,
                                },
                            }}
                        />
                    </Box>
                )}

                <Typography
                    variant="caption"
                    sx={{ color: vortxColors.stone, mt: 2, display: 'block', textAlign: 'center' }}
                >
                    You can navigate away - extraction runs in the background
                </Typography>
            </Paper>
        );
    }

    // --- COMPLETE STATE ---
    if (status === 'complete') {
        return (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: vortxColors.charcoal }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: '#2ecc71', mb: 2 }} />
                <Typography variant="h6" sx={{ color: vortxColors.white }}>
                    Extraction Complete!
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleReset}
                    sx={{
                        mt: 3,
                        background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                        color: isDark ? '#FFFFFF' : '#000',
                        fontWeight: 600,
                        '&:hover': {
                            background: `linear-gradient(135deg, #c9a060 0%, #e0b585 100%)`,
                        },
                    }}
                >
                    Upload Another
                </Button>
            </Paper>
        );
    }

    // --- ERROR STATE ---
    if (status === 'error') {
        return (
            <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'An unexpected error occurred'}
                </Alert>
                <Button
                    variant="contained"
                    onClick={handleReset}
                    sx={{
                        background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                        color: isDark ? '#FFFFFF' : '#000',
                        fontWeight: 600,
                        '&:hover': {
                            background: `linear-gradient(135deg, #c9a060 0%, #e0b585 100%)`,
                        },
                    }}
                >
                    Try Again
                </Button>
            </Box>
        );
    }

    return null;
}

DocumentUploader.propTypes = {
    defaultDocumentType: PropTypes.string,
    allowedDocumentTypes: PropTypes.array,
    hideDocumentType: PropTypes.bool,
    projectId: PropTypes.string,
    metadata: PropTypes.object,
    onExtractionComplete: PropTypes.func,
    onError: PropTypes.func,
    title: PropTypes.string,
    description: PropTypes.string,
    acceptedFileTypes: PropTypes.string,
};
